import React, { useReducer, createContext, useContext } from 'react';
import { FarmDeliveries } from 'types/FarmData';
import { ProductData } from 'types/ProductData';
import { validateProduct } from 'utils/productValidationHelper';
import {
  calculateFarmPriceTotals,
  calculateTotalPrice,
  FarmProductPriceTotalProps,
} from 'utils/productTotalPriceHelper';
import { CouponDiscounts } from '../../interfaces/Coupon';
import { WHOLESALE_PREFIX } from 'enums/publicRoutes';

type CartStorageKey = 'farmProducts' | 'priceTotal' | 'coupons' | 'cartId' | 'cartName';

function getMarketSuffix(): string {
  return window.location.pathname.includes(WHOLESALE_PREFIX) ? '_WHOLESALE' : '_EMARKET';
}

export const PAYMENT_IN_PROGRESS_KEY = 'paymentInProgress';
export const PAYMENT_IN_PROGRESS_TTL_MS = 30 * 60 * 1000;
const PENDING_PAYMENT_CART_KEY = 'pendingPaymentCart';
export const CART_RESTORE_PENDING_KEY = 'cartRestorePending';

const CART_CONTENT_KEYS: CartStorageKey[] = ['farmProducts', 'priceTotal', 'coupons', 'cartId', 'cartName'];
const MARKET_SUFFIXES = ['_EMARKET', '_WHOLESALE'];

export function clearCartStorage(): void {
  MARKET_SUFFIXES.forEach((suffix) => {
    CART_CONTENT_KEYS.forEach((key) => localStorage.removeItem(`${key}${suffix}`));
  });
  localStorage.removeItem('wholesaleDeliveryMode');
  localStorage.removeItem('wholesaleDeliveries');
}

export interface PaymentInProgress {
  at: number;
  marketType: string;
}

export interface PendingPaymentCart {
  farmProducts: FarmProduct[];
  priceTotal: number;
  coupons: CouponDiscounts[];
  cartId?: number | null;
}

function marketSuffixFor(marketType: string): string {
  return marketType === 'WHOLESALE' ? '_WHOLESALE' : '_EMARKET';
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch (e) {
    return fallback;
  }
}

export function stashCartForPayment(marketType: string): void {
  const suffix = marketSuffixFor(marketType);
  const rawCartId = localStorage.getItem(`cartId${suffix}`);
  const backup: PendingPaymentCart = {
    farmProducts: readJson<FarmProduct[]>(`farmProducts${suffix}`, []),
    priceTotal: readJson<number>(`priceTotal${suffix}`, 0),
    coupons: readJson<CouponDiscounts[]>(`coupons${suffix}`, []),
    cartId: rawCartId ? parseInt(rawCartId, 10) : null,
  };

  localStorage.setItem(`${PENDING_PAYMENT_CART_KEY}${suffix}`, JSON.stringify(backup));
  CART_CONTENT_KEYS.forEach((key) => localStorage.removeItem(`${key}${suffix}`));
  localStorage.setItem(
    PAYMENT_IN_PROGRESS_KEY,
    JSON.stringify({ at: Date.now(), marketType } as PaymentInProgress)
  );
}

export function readPaymentInProgress(): PaymentInProgress | null {
  const flag = readJson<PaymentInProgress | null>(PAYMENT_IN_PROGRESS_KEY, null);

  if (!flag || typeof flag.at !== 'number' || Date.now() - flag.at > PAYMENT_IN_PROGRESS_TTL_MS) {
    localStorage.removeItem(PAYMENT_IN_PROGRESS_KEY);
    return null;
  }

  return flag;
}

export function takePendingPaymentCart(marketType: string): PendingPaymentCart | null {
  const key = `${PENDING_PAYMENT_CART_KEY}${marketSuffixFor(marketType)}`;
  const pending = readJson<PendingPaymentCart | null>(key, null);
  localStorage.removeItem(key);

  return pending && Array.isArray(pending.farmProducts) && pending.farmProducts.length > 0 ? pending : null;
}

export function getCartStorageKey(key: CartStorageKey): string {
  return `${key}${getMarketSuffix()}`;
}

export interface ParcelLocation {
  id?: number;
  name?: string;
  address?: string;
}

export interface SelectedDelivery {
  id?: number;
  city?: string;
  address?: string;
  firm_name?: string;
  name?: string;
  selectedDeliveryId?: number;
  price: number | null;
}

export type WholesaleDeliveryMode = 'single' | 'detailed';

export interface WholesaleDeliveryProduct {
  product_id: number;
  farm_id: number;
  quantity: number;
}

export interface WholesaleDeliveryCondition {
  id: string;
  delivery_address: string;
  delivery_start_date?: string;
  delivery_end_date?: string;
  products?: WholesaleDeliveryProduct[];
}

export interface CartProduct {
  cartProduct?: ProductData;
  amount: number;
  vat?: number;
  stock_total?: number;
  requires_auth?: boolean;
  cartType?: 'EMARKET' | 'WHOLESALE';
}

export interface FarmProduct {
  farm_id: number;
  farm_name?: string;
  farmPriceTotals: FarmProductPriceTotalProps;
  products: CartProduct[];
  farmDeliveries: FarmDeliveries[] | undefined;
  selectedDelivery?: SelectedDelivery;
  transaction_status?: string;
  transaction_type?: string | null;
}

export interface LastAddedItem {
  product: ProductData;
  amount: number;
  addedAt: number;
}

interface CartState {
  farmProducts: FarmProduct[];
  priceTotal: number;
  coupons: CouponDiscounts[];
  requiresAuth: boolean;
  cartId?: number;
  cartName?: string;
  marketType: string;
  wholesaleDeliveryMode?: WholesaleDeliveryMode;
  wholesaleDeliveries?: WholesaleDeliveryCondition[];
  lastAddedItem?: LastAddedItem;
}

export enum CartActionType {
  ADD_ITEM = 'ADD_ITEM',
  REMOVE_ITEM = 'REMOVE_ITEM',
  CLEAR_CART = 'CLEAR_CART',
  SWITCH_MARKET = 'SWITCH_MARKET',
  REFETCH_CART_PRODUCTS = 'REFETCH_CART_PRODUCTS',
  RESTORE_CART = 'RESTORE_CART',
  SET_DELIVERY = 'SET_DELIVERY',
  REMOVE_ALL_SELECTED_DELIVERIES = 'REMOVE_ALL_SELECTED_DELIVERIES',
  ADD_COUPON = 'ADD_COUPON',
  REMOVE_COUPON = 'REMOVE_COUPON',
  REMOVE_ALL_COUPONS = 'REMOVE_ALL_COUPONS',
  AUTHORIZE_CART = 'AUTHORIZE_CART',
  SET_WHOLESALE_DELIVERY_MODE = 'SET_WHOLESALE_DELIVERY_MODE',
  SET_WHOLESALE_DELIVERIES = 'SET_WHOLESALE_DELIVERIES',
  ADD_WHOLESALE_DELIVERY = 'ADD_WHOLESALE_DELIVERY',
  UPDATE_WHOLESALE_DELIVERY = 'UPDATE_WHOLESALE_DELIVERY',
  REMOVE_WHOLESALE_DELIVERY = 'REMOVE_WHOLESALE_DELIVERY',
}

type CartAction =
  | {
      type: CartActionType.ADD_ITEM;
      product: ProductData | undefined;
      amount: number;
    }
  | {
      type: CartActionType.REMOVE_ITEM;
      farmId: number;
      productId: number;
    }
  | { type: CartActionType.CLEAR_CART }
  | ({ type: CartActionType.RESTORE_CART } & PendingPaymentCart)
  | { type: CartActionType.SWITCH_MARKET; marketType: 'EMARKET' | 'WHOLESALE' }
  | {
      type: CartActionType.REFETCH_CART_PRODUCTS;
      farmProducts: FarmProduct[];
      priceTotal: number;
      requiresAuth: boolean;
      cartId?: number;
      cartName?: string;
      marketType: string;
    }
  | {
      type: CartActionType.SET_DELIVERY;
      farmId: number;
      delivery: SelectedDelivery;
    }
  | { type: CartActionType.REMOVE_ALL_SELECTED_DELIVERIES }
  | { type: CartActionType.ADD_COUPON; coupon: CouponDiscounts }
  | { type: CartActionType.REMOVE_COUPON; code: string }
  | { type: CartActionType.REMOVE_ALL_COUPONS }
  | { type: CartActionType.AUTHORIZE_CART }
  | { type: CartActionType.SET_WHOLESALE_DELIVERY_MODE; mode: WholesaleDeliveryMode }
  | { type: CartActionType.SET_WHOLESALE_DELIVERIES; deliveries: WholesaleDeliveryCondition[] }
  | { type: CartActionType.ADD_WHOLESALE_DELIVERY; delivery: WholesaleDeliveryCondition }
  | { type: CartActionType.UPDATE_WHOLESALE_DELIVERY; delivery: WholesaleDeliveryCondition }
  | { type: CartActionType.REMOVE_WHOLESALE_DELIVERY; id: string };

const CartStateContext = createContext<CartState | undefined>(undefined);
const CartDispatchContext = createContext<
  React.Dispatch<CartAction> | undefined
>(undefined);

function cartReducer(state: CartState, action: CartAction): CartState {
  const pathname = window.location.pathname;
  const isWholeSale = pathname.includes(WHOLESALE_PREFIX);

  switch (action.type) {
    case CartActionType.ADD_ITEM: {
      const { product: newProduct, amount } = action;
      let requiresAuth = false;
      let lastAddedAmount = 0;
      if (!newProduct) return state;

      const updatedFarmProducts: FarmProduct[] = [...state.farmProducts];

      let farmProduct = updatedFarmProducts.find(
        (farmProduct) => farmProduct.farm_id === newProduct.farm_id
      );

      if (farmProduct) {
        let cartProduct = farmProduct.products.find(
          (cartProduct) => cartProduct.cartProduct?.id === newProduct.id
        );

        if (cartProduct) {
          const validatedProduct = validateProduct({
            product: newProduct,
            count: Number((cartProduct.amount + amount).toFixed(3)),
            marketType: isWholeSale ? 'WHOLESALE' : 'EMARKET',
          });

          cartProduct.stock_total = validatedProduct.stock_total;
          cartProduct.amount = validatedProduct.stock_count;
          cartProduct.requires_auth = validatedProduct.requires_auth;
          cartProduct.cartType = isWholeSale ? 'WHOLESALE' : 'EMARKET';
          lastAddedAmount = validatedProduct.stock_count;

          if (validatedProduct.requires_auth) {
            requiresAuth = true;
          }
        } else {
          const validatedProduct = validateProduct({
            product: newProduct,
            count: amount,
            marketType: isWholeSale ? 'WHOLESALE' : 'EMARKET',
          });

          const newCartProduct: CartProduct = {
            cartProduct: newProduct,
            amount: validatedProduct.stock_count,
            stock_total: validatedProduct.stock_total,
            requires_auth: validatedProduct.requires_auth,
            cartType: isWholeSale ? 'WHOLESALE' : 'EMARKET',
          };

          farmProduct.products.push(newCartProduct);
          lastAddedAmount = validatedProduct.stock_count;

          if (validatedProduct.requires_auth) {
            state.requiresAuth = true;
          }
        }

        const farmPriceTotals = calculateFarmPriceTotals(
          farmProduct.farm_id,
          farmProduct.products,
          state.coupons
        );

        farmProduct.farmPriceTotals = farmPriceTotals;
      } else {
        const validatedProduct = validateProduct({
          product: newProduct,
          count: amount,
          marketType: isWholeSale ? 'WHOLESALE' : 'EMARKET',
        });

        const products: CartProduct[] = [
          {
            cartProduct: newProduct,
            amount: validatedProduct.stock_count,
            stock_total: validatedProduct.stock_total,
            requires_auth: validatedProduct.requires_auth,
            cartType: isWholeSale ? 'WHOLESALE' : 'EMARKET',
          },
        ];

        const farmPriceTotals = calculateFarmPriceTotals(
          newProduct.farm_id,
          products,
          state.coupons
        );

        const newFarmProduct: FarmProduct = {
          farm_id: newProduct.farm_id,
          farm_name: newProduct.farm_name,
          farmDeliveries: newProduct.farm?.deliveries,
          products,
          farmPriceTotals,
        };

        updatedFarmProducts.push(newFarmProduct);
        lastAddedAmount = validatedProduct.stock_count;

        if (validatedProduct.requires_auth) {
          requiresAuth = true;
        }
      }

      const newPriceTotal = calculateTotalPrice(updatedFarmProducts);

      localStorage.setItem(getCartStorageKey('farmProducts'), JSON.stringify(updatedFarmProducts));
      localStorage.setItem(getCartStorageKey('priceTotal'), JSON.stringify(newPriceTotal));

      state.requiresAuth = requiresAuth;

      return {
        ...state,
        farmProducts: updatedFarmProducts,
        priceTotal: newPriceTotal,
        lastAddedItem:
          amount > 0 && lastAddedAmount > 0
            ? {
                product: newProduct,
                amount: lastAddedAmount,
                addedAt: Date.now(),
              }
            : state.lastAddedItem,
      };
    }

    case CartActionType.REMOVE_ITEM: {
      const { farmId, productId } = action;
      const updatedFarmProducts = state.farmProducts.reduce<FarmProduct[]>(
        (acc, farm) => {
          if (farm.farm_id === farmId) {
            const updatedProducts = farm.products.filter(
              (cartProduct) => cartProduct.cartProduct?.id !== productId
            );

            if (!updatedProducts.length) {
              return acc;
            }

            const farmPriceTotals = calculateFarmPriceTotals(
              farmId,
              updatedProducts,
              state.coupons
            );

            acc.push({
              ...farm,
              products: updatedProducts,
              farmPriceTotals,
            });
          } else {
            acc.push(farm);
          }

          return acc;
        },
        []
      );

      const remainingProductIds = updatedFarmProducts
        .flatMap((farm) => farm.products.map((p) => p.cartProduct?.id))
        .filter((id): id is number => !!id);
      const updatedCoupons = state.coupons.filter((c) =>
        Object.keys(c.productDiscounts ?? {}).some((pid) =>
          remainingProductIds.includes(Number(pid))
        )
      );
      const recalculatedFarmProducts = updatedFarmProducts.map((farm) => ({
        ...farm,
        farmPriceTotals: calculateFarmPriceTotals(
          farm.farm_id,
          farm.products,
          updatedCoupons
        ),
      }));

      const newPriceTotal = calculateTotalPrice(recalculatedFarmProducts);

      localStorage.setItem(
        getCartStorageKey('farmProducts'),
        JSON.stringify(recalculatedFarmProducts)
      );
      localStorage.setItem(getCartStorageKey('priceTotal'), JSON.stringify(newPriceTotal));
      localStorage.setItem(getCartStorageKey('coupons'), JSON.stringify(updatedCoupons));

      state.requiresAuth = updatedFarmProducts.some((f) =>
        f.products.some((p) => p.requires_auth)
      );

      if (recalculatedFarmProducts.length === 0) {
        localStorage.removeItem('wholesaleDeliveryMode');
        localStorage.removeItem('wholesaleDeliveries');

        return {
          ...state,
          farmProducts: recalculatedFarmProducts,
          priceTotal: newPriceTotal,
          coupons: updatedCoupons,
          wholesaleDeliveryMode: undefined,
          wholesaleDeliveries: [],
        };
      }

      return {
        ...state,
        farmProducts: recalculatedFarmProducts,
        priceTotal: newPriceTotal,
        coupons: updatedCoupons,
      };
    }

    case CartActionType.CLEAR_CART: {
      localStorage.removeItem(getCartStorageKey('farmProducts'));
      localStorage.removeItem(getCartStorageKey('priceTotal'));
      localStorage.removeItem(getCartStorageKey('coupons'));
      localStorage.removeItem(getCartStorageKey('cartId'));
      localStorage.removeItem(getCartStorageKey('cartName'));
      localStorage.removeItem('wholesaleDeliveryMode');
      localStorage.removeItem('wholesaleDeliveries');
      localStorage.setItem('marketType', isWholeSale ? 'WHOLESALE' : 'EMARKET');

      return {
        farmProducts: [],
        priceTotal: 0,
        coupons: [],
        requiresAuth: false,
        marketType: isWholeSale ? 'WHOLESALE' : 'EMARKET',
        wholesaleDeliveryMode: undefined,
        wholesaleDeliveries: [],
      };
    }

    case CartActionType.SWITCH_MARKET: {
      const suffix = action.marketType === 'WHOLESALE' ? '_WHOLESALE' : '_EMARKET';
      const farmProducts = localStorage.getItem(`farmProducts${suffix}`)
        ? JSON.parse(localStorage.getItem(`farmProducts${suffix}`) || '[]')
        : [];
      const priceTotal = localStorage.getItem(`priceTotal${suffix}`)
        ? JSON.parse(localStorage.getItem(`priceTotal${suffix}`) || '0')
        : 0;
      const coupons = localStorage.getItem(`coupons${suffix}`)
        ? JSON.parse(localStorage.getItem(`coupons${suffix}`) || '[]')
        : [];
      const cartId = localStorage.getItem(`cartId${suffix}`)
        ? parseInt(localStorage.getItem(`cartId${suffix}`)!)
        : undefined;
      const cartName = localStorage.getItem(`cartName${suffix}`) || '';

      localStorage.setItem('marketType', action.marketType);

      return {
        ...state,
        farmProducts,
        priceTotal,
        coupons,
        requiresAuth: false,
        cartId,
        cartName,
        marketType: action.marketType,
        wholesaleDeliveryMode: action.marketType === 'WHOLESALE'
          ? (localStorage.getItem('wholesaleDeliveryMode') as WholesaleDeliveryMode | undefined)
          : undefined,
        wholesaleDeliveries: action.marketType === 'WHOLESALE'
          ? (localStorage.getItem('wholesaleDeliveries')
              ? JSON.parse(localStorage.getItem('wholesaleDeliveries') || '[]')
              : [])
          : [],
      };
    }

    case CartActionType.REFETCH_CART_PRODUCTS: {
      const {
        farmProducts,
        priceTotal,
        requiresAuth,
        cartId,
        cartName,
        marketType,
      } = action;

      localStorage.setItem(getCartStorageKey('farmProducts'), JSON.stringify(farmProducts));
      localStorage.setItem(getCartStorageKey('priceTotal'), JSON.stringify(priceTotal));
      if (cartId) {
        localStorage.setItem(getCartStorageKey('cartId'), cartId.toString());
      } else {
        localStorage.removeItem(getCartStorageKey('cartId'));
      }
      localStorage.setItem(getCartStorageKey('cartName'), cartName || '');
      localStorage.setItem('marketType', marketType || '');

      state.requiresAuth = requiresAuth;

      return {
        ...state,
        farmProducts: farmProducts,
        priceTotal: priceTotal,
        cartId: cartId,
        cartName: cartName,
        marketType: marketType,
      };
    }

    case CartActionType.RESTORE_CART: {
      const { farmProducts, priceTotal, coupons } = action;

      localStorage.setItem(getCartStorageKey('farmProducts'), JSON.stringify(farmProducts));
      localStorage.setItem(getCartStorageKey('priceTotal'), JSON.stringify(priceTotal));
      localStorage.setItem(getCartStorageKey('coupons'), JSON.stringify(coupons));
      localStorage.removeItem(getCartStorageKey('cartId'));
      localStorage.removeItem(getCartStorageKey('cartName'));

      return {
        ...state,
        farmProducts,
        priceTotal,
        coupons,
        cartId: undefined,
        cartName: '',
        requiresAuth: farmProducts.some((f) => f.products.some((p) => p.requires_auth)),
      };
    }

    case CartActionType.SET_DELIVERY: {
      const { farmId, delivery } = action;

      const updatedFarmProducts = state.farmProducts.map((farmProduct) => {
        if (farmProduct.farm_id === farmId) {
          return {
            ...farmProduct,
            selectedDelivery: delivery,
          };
        }
        return farmProduct;
      });

      const newPriceTotal = calculateTotalPrice(updatedFarmProducts);

      localStorage.setItem(getCartStorageKey('farmProducts'), JSON.stringify(updatedFarmProducts));
      localStorage.setItem(getCartStorageKey('priceTotal'), JSON.stringify(newPriceTotal));

      return {
        ...state,
        farmProducts: updatedFarmProducts,
        priceTotal: newPriceTotal,
      };
    }

    case CartActionType.REMOVE_ALL_SELECTED_DELIVERIES: {
      const updatedFarmProducts = state.farmProducts.map((farmProduct) => {
        return {
          ...farmProduct,
          selectedDelivery: undefined,
        };
      });

      const newPriceTotal = calculateTotalPrice(updatedFarmProducts);

      localStorage.setItem(getCartStorageKey('farmProducts'), JSON.stringify(updatedFarmProducts));
      localStorage.setItem(getCartStorageKey('priceTotal'), JSON.stringify(newPriceTotal));

      return {
        ...state,
        farmProducts: updatedFarmProducts,
        priceTotal: newPriceTotal,
      };
    }

    case CartActionType.ADD_COUPON: {
      const addedCoupons = [...state.coupons, action.coupon];

      const updatedFarmProducts = state.farmProducts.map((farm) => {
        return {
          ...farm,
          farmPriceTotals: calculateFarmPriceTotals(
            farm.farm_id,
            farm.products,
            addedCoupons
          ),
        };
      });

      const newTotal = calculateTotalPrice(updatedFarmProducts);
      localStorage.setItem(getCartStorageKey('coupons'), JSON.stringify(addedCoupons));
      localStorage.setItem(getCartStorageKey('farmProducts'), JSON.stringify(updatedFarmProducts));
      localStorage.setItem(getCartStorageKey('priceTotal'), JSON.stringify(newTotal));

      return {
        ...state,
        coupons: addedCoupons,
        farmProducts: updatedFarmProducts,
        priceTotal: newTotal,
      };
    }

    case CartActionType.REMOVE_COUPON: {
      const remainingCoupons = state.coupons.filter(
        (c) => c.code !== action.code
      );
      const updatedFarmProducts = state.farmProducts.map((farm) => ({
        ...farm,
        farmPriceTotals: calculateFarmPriceTotals(
          farm.farm_id,
          farm.products,
          remainingCoupons
        ),
      }));
      const newTotal = calculateTotalPrice(updatedFarmProducts);
      localStorage.setItem(getCartStorageKey('coupons'), JSON.stringify(remainingCoupons));
      localStorage.setItem(getCartStorageKey('farmProducts'), JSON.stringify(updatedFarmProducts));
      localStorage.setItem(getCartStorageKey('priceTotal'), JSON.stringify(newTotal));

      return {
        ...state,
        coupons: remainingCoupons,
        farmProducts: updatedFarmProducts,
        priceTotal: newTotal,
      };
    }
    case CartActionType.REMOVE_ALL_COUPONS: {
      const updatedFarmProducts = state.farmProducts.map((farm) => ({
        ...farm,
        farmPriceTotals: calculateFarmPriceTotals(farm.farm_id, farm.products),
      }));

      const newTotal = calculateTotalPrice(updatedFarmProducts);
      localStorage.removeItem(getCartStorageKey('coupons'));
      localStorage.setItem(getCartStorageKey('farmProducts'), JSON.stringify(updatedFarmProducts));
      localStorage.setItem(getCartStorageKey('priceTotal'), JSON.stringify(newTotal));

      return {
        ...state,
        coupons: [],
        farmProducts: updatedFarmProducts,
        priceTotal: newTotal,
      };
    }

    case CartActionType.AUTHORIZE_CART:
      state.requiresAuth = false;

      return { ...state };

    case CartActionType.SET_WHOLESALE_DELIVERY_MODE: {
      const { mode } = action;
      localStorage.setItem('wholesaleDeliveryMode', mode);
      if (mode === 'single') {
        localStorage.removeItem('wholesaleDeliveries');
        return {
          ...state,
          wholesaleDeliveryMode: mode,
          wholesaleDeliveries: [],
        };
      }
      return {
        ...state,
        wholesaleDeliveryMode: mode,
      };
    }

    case CartActionType.SET_WHOLESALE_DELIVERIES: {
      const { deliveries } = action;
      localStorage.setItem('wholesaleDeliveries', JSON.stringify(deliveries));
      return {
        ...state,
        wholesaleDeliveries: deliveries,
      };
    }

    case CartActionType.ADD_WHOLESALE_DELIVERY: {
      const { delivery } = action;
      const updatedDeliveries = [...(state.wholesaleDeliveries || []), delivery];
      localStorage.setItem('wholesaleDeliveries', JSON.stringify(updatedDeliveries));
      return {
        ...state,
        wholesaleDeliveries: updatedDeliveries,
      };
    }

    case CartActionType.UPDATE_WHOLESALE_DELIVERY: {
      const { delivery } = action;
      const updatedDeliveries = (state.wholesaleDeliveries || []).map((d) =>
        d.id === delivery.id ? delivery : d
      );
      localStorage.setItem('wholesaleDeliveries', JSON.stringify(updatedDeliveries));
      return {
        ...state,
        wholesaleDeliveries: updatedDeliveries,
      };
    }

    case CartActionType.REMOVE_WHOLESALE_DELIVERY: {
      const { id } = action;
      const updatedDeliveries = (state.wholesaleDeliveries || []).filter(
        (d) => d.id !== id
      );
      localStorage.setItem('wholesaleDeliveries', JSON.stringify(updatedDeliveries));
      return {
        ...state,
        wholesaleDeliveries: updatedDeliveries,
      };
    }
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  if (localStorage.getItem('farmProducts') && !localStorage.getItem(getCartStorageKey('farmProducts'))) {
    (['farmProducts', 'priceTotal', 'coupons', 'cartId', 'cartName'] as CartStorageKey[]).forEach((key) => {
      const value = localStorage.getItem(key);
      if (value) localStorage.setItem(getCartStorageKey(key), value);
      localStorage.removeItem(key);
    });
  }

  const initialState: CartState = {
    farmProducts: localStorage.getItem(getCartStorageKey('farmProducts'))
      ? JSON.parse(localStorage.getItem(getCartStorageKey('farmProducts')) || '[]')
      : [],
    priceTotal: localStorage.getItem(getCartStorageKey('priceTotal'))
      ? JSON.parse(localStorage.getItem(getCartStorageKey('priceTotal')) || '0')
      : 0,
    coupons: localStorage.getItem(getCartStorageKey('coupons'))
      ? JSON.parse(localStorage.getItem(getCartStorageKey('coupons')) || '[]')
      : [],
    requiresAuth: false,
    cartId: localStorage.getItem(getCartStorageKey('cartId'))
      ? parseInt(localStorage.getItem(getCartStorageKey('cartId'))!)
      : undefined,
    cartName: localStorage.getItem(getCartStorageKey('cartName')) || '',
    marketType: localStorage.getItem('marketType') || 'EMARKET',
    wholesaleDeliveryMode: localStorage.getItem('wholesaleDeliveryMode') as WholesaleDeliveryMode | undefined,
    wholesaleDeliveries: localStorage.getItem('wholesaleDeliveries')
      ? JSON.parse(localStorage.getItem('wholesaleDeliveries') || '[]')
      : [],
  };

  const [state, dispatch] = useReducer(cartReducer, initialState);

  return (
    <CartStateContext.Provider value={state}>
      <CartDispatchContext.Provider value={dispatch}>
        {children}
      </CartDispatchContext.Provider>
    </CartStateContext.Provider>
  );
}

export function useCartState() {
  const context = useContext(CartStateContext);
  if (context === undefined) {
    throw new Error('useCartState must be used within a CartProvider');
  }
  return context;
}

export function useCartDispatch() {
  const context = useContext(CartDispatchContext);
  if (context === undefined) {
    throw new Error('useCartDispatch must be used within a CartProvider');
  }
  return context;
}
